import React from 'react';
import {
    Box,
    InputAdornment,
    List,
    ListItemButton,
    ListItemIcon,
    ListItemText,
    TextField,
    Typography,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import { searchAdminNavigation, type AdminNavMatch } from '../../lib/roles';
import { getIconComponent } from './NavigationIcons';

interface AdminNavSearchProps {
    permissions: string[];
    query: string;
    onQueryChange: (query: string) => void;
    /** Called with the chosen page; the caller navigates and closes its menu or dialog. */
    onSelect: (match: AdminNavMatch) => void;
    /** Shown while nothing is typed (e.g. recent pages). */
    emptyItems?: AdminNavMatch[];
    emptyTitle?: string;
    autoFocus?: boolean;
}

/**
 * Type-to-search over every admin page the user can open, across all groups. Arrow keys move the highlight, Enter
 * opens it, Escape clears the query. Used at the top of the Admin menu and in the Cmd/Ctrl+K palette.
 */
export function AdminNavSearch({
    permissions,
    query,
    onQueryChange,
    onSelect,
    emptyItems = [],
    emptyTitle,
    autoFocus = false,
}: AdminNavSearchProps) {
    const [highlighted, setHighlighted] = React.useState(0);
    // Punctuation alone ("&", "-") is no search at all.
    const searching = /[a-z0-9]/i.test(query);
    const results = React.useMemo(
        () => (searching ? searchAdminNavigation(permissions, query) : emptyItems),
        [permissions, query, emptyItems, searching],
    );
    const listId = `admin-nav-search-${React.useId().replace(/:/g, '')}`;
    const listRef = React.useRef<HTMLUListElement>(null);

    React.useEffect(() => {
        setHighlighted(0);
    }, [query]);

    // Keep the highlighted page visible as the arrow keys move through a long list.
    React.useEffect(() => {
        const el = listRef.current?.querySelector<HTMLElement>(`#${listId}-${highlighted}`);
        el?.scrollIntoView?.({ block: 'nearest' });
    }, [highlighted, listId]);

    const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        // Escape with nothing typed closes the menu or dialog around us: let it through.
        if (event.key === 'Escape' && !query) return;
        // Keep other keystrokes in the field: the surrounding MUI Menu would use them to jump between items.
        event.stopPropagation();
        if (event.nativeEvent.isComposing) return; // Enter that confirms an IME composition isn't a choice
        if (event.key === 'ArrowDown') {
            event.preventDefault();
            setHighlighted((i) => (results.length ? (i + 1) % results.length : 0));
        } else if (event.key === 'ArrowUp') {
            event.preventDefault();
            setHighlighted((i) => (results.length ? (i - 1 + results.length) % results.length : 0));
        } else if (event.key === 'Enter') {
            const match = results[highlighted];
            if (match) {
                event.preventDefault();
                onSelect(match);
            }
        } else if (event.key === 'Escape' && query) {
            event.preventDefault();
            onQueryChange('');
        }
    };

    return (
        <Box>
            <Box sx={{ px: 2, py: 1 }}>
                <TextField
                    fullWidth
                    size="small"
                    placeholder="Search admin pages…"
                    value={query}
                    autoFocus={autoFocus}
                    onChange={(e) => onQueryChange(e.target.value)}
                    onKeyDown={handleKeyDown}
                    inputProps={{
                        'aria-label': 'Search admin pages',
                        'aria-controls': listId,
                        'aria-activedescendant': results[highlighted] ? `${listId}-${highlighted}` : undefined,
                    }}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon fontSize="small" />
                            </InputAdornment>
                        ),
                    }}
                />
            </Box>
            {(searching || results.length > 0) && (
                <List id={listId} ref={listRef} role="listbox" dense disablePadding aria-label="Admin pages">
                    {!searching && emptyTitle && results.length > 0 && (
                        <Typography variant="overline" color="text.secondary" sx={{ px: 2, display: 'block' }}>
                            {emptyTitle}
                        </Typography>
                    )}
                    {results.map((match, i) => (
                        <ListItemButton
                            key={match.path}
                            id={`${listId}-${i}`}
                            role="option"
                            aria-selected={i === highlighted}
                            selected={i === highlighted}
                            onMouseEnter={() => setHighlighted(i)}
                            onClick={() => onSelect(match)}
                            sx={{ px: 2 }}
                        >
                            <ListItemIcon sx={{ minWidth: 32 }}>{getIconComponent(match.label)}</ListItemIcon>
                            <ListItemText
                                primary={match.label}
                                secondary={match.categoryLabel}
                                primaryTypographyProps={{ variant: 'body2' }}
                                secondaryTypographyProps={{ variant: 'caption' }}
                            />
                        </ListItemButton>
                    ))}
                    {searching && results.length === 0 && (
                        <Typography variant="body2" color="text.secondary" sx={{ px: 2, py: 1.5 }}>
                            No admin page matches “{query.trim()}”
                        </Typography>
                    )}
                </List>
            )}
        </Box>
    );
}
